/* eslint-disable no-param-reassign */
import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import billingService from '../services/billingService';

export const createPayment = createAsyncThunk(
  'billing/createPayment',
  async ({ superOrganizationId, currency = 'USD', includeBankAccount = false, isCreditCardTrial = false }) => {
    const setupIntent = await billingService.createSetupIntent(superOrganizationId, currency, includeBankAccount, isCreditCardTrial);

    return setupIntent;
  }
);

export const getNextBillingDate = createAsyncThunk('billing/getNextBillingDate', async (superOrganizationId) => {
  const date = await billingService.getNextBillingDate(superOrganizationId);

  return date;
});

export const getProPrice = createAsyncThunk(
  'billing/getProPrice',
  async ({ superOrganizationId, planPeriod = 'monthly', currency = 'USD' }) => {
    const price = await billingService.getProPrice(superOrganizationId, planPeriod, currency);

    return price;
  }
);

export const getEssPrice = createAsyncThunk('billing/getEssPrice', async ({ priceId, sfdcAccountId, plan }) => {
  const price = await billingService.getEssPrice(priceId, sfdcAccountId, plan);

  return price;
});

export const getSuperOrganizationInfo = createAsyncThunk('billing/getSuperOrganizationInfo', async (superOrganizationId) => {
  const totalUsers = await billingService.getSuperOrganizationInfo(superOrganizationId);

  return totalUsers;
});

export const getCredits = createAsyncThunk('billing/getCredits', async (superOrganizationId) => {
  const credits = await billingService.getCredits(superOrganizationId);

  return credits;
});

export const changePlan = createAsyncThunk(
  'billing/changePlan',
  async ({ superOrganizationId, planPeriod, planType, overrideSeatCount, sfdcAccountId, accountName }) => {
    const plan = await billingService.changePlan(superOrganizationId, planPeriod, planType, overrideSeatCount, sfdcAccountId, accountName);

    return plan;
  }
);

export const changeSubscriptionPlan = createAsyncThunk(
  'billing/changeSubscriptionPlan',
  async ({ superOrganizationId, newPlanPeriod, newPlanType, currency = 'USD' }) => {
    const plan = await billingService.changeSubscriptionPlan(superOrganizationId, newPlanPeriod, newPlanType, currency);

    return plan;
  }
);

export const downgrade = createAsyncThunk(
  'billing/downgrade',
  async ({ superOrganizationId, upgradeReason = '', downgradeReason = '', explanation = '' }) => {
    const res = await billingService.downgrade(superOrganizationId, upgradeReason, downgradeReason, explanation);

    return { ...res, superOrganizationId };
  }
);

export const reactivate = createAsyncThunk('billing/reactivate', async ({ superOrganizationId }) => {
  const res = await billingService.reactivate(superOrganizationId);

  return { ...res, superOrganizationId };
});

export const applyPromotion = createAsyncThunk('billing/applyPromotion', async ({ superOrganizationId, promotionCode }) => {
  const promotion = await billingService.applyPromotion(superOrganizationId, promotionCode);

  return promotion;
});

export const updateCustomerAddress = createAsyncThunk('billing/updateCustomerAddress', async ({ userId, superOrganizationId, address }) => {
  await billingService.updateCustomerAddress(userId, superOrganizationId, address);
});

export const processBaseUpgradeWithInvoice = createAsyncThunk(
  'billing/processBaseUpgradeWithInvoice',
  async ({ memoInfo, billingEmail, numPurchasedSeats, taxData, taxCalculationError, taxExempt, billingDetails, poNumber }) => {
    const result = await billingService.processBaseUpgradeWithInvoice(
      memoInfo,
      billingEmail,
      numPurchasedSeats,
      taxData,
      taxCalculationError,
      billingDetails,
      taxExempt,
      poNumber
    );

    return result;
  }
);

export const clearPromoCodeError = createAction('billing/clearPromoCodeError');

export const addPoNumber = createAsyncThunk('billing/addPoNumber', async ({ userId, superOrganizationId, poNumber }) => {
  await billingService.addPoNumber(userId, superOrganizationId, poNumber);
});

export const INITIAL_STATE = {
  clientSecret: '',
  totalUsers: 0,
  totalDocs: 0,
  proPrice: 0,
  entPriceItems: [],
  monthlyProPrice: 0,
  annualProPrice: 0,
  monthlyProTeamsUnitPrice: 0,
  annualProTeamsUnitPrice: 0,
  sessionUrl: '',
  nextBillingDate: '',
  scribeProPriceId: '',
  currentPlan: 'annual',
  currentPlanType: 'individual',
  credits: 0,
  organizations: [],
  invoiceData: null,
  promotion: '',
  promotionStart: '',
  promotionDuration: 0,
  promotionPercentOff: 0,
  promotionTaxExempt: false,
  isCancelled: false,
  activePromotion: false,
  activeTrial: false,
  currentPeriodEnd: '',
  nextInvoiceDate: '',
  promotionActiveNextPeriod: false,
  overrideSeatCount: 0,
  purchasedSeats: 0,
  isLoading: false,
  promoCodeError: false,
  isError: false,
  isLoadingPromo: false,
  isLoadingPrice: false,
  isLoadingPlan: false,
  isLoadingInfo: false,
  isLoadingPayment: false,
  errorMessage: null,
  updateAddressError: false,
  updateAddressPending: false,
  updateAddressSuccess: false,
  hasBillingAddress: true,
  addPoNumberError: false,
  addPoNumberPending: false,
  addPoNumberSuccess: false
};

export const billingSlice = createSlice({
  name: 'billing',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createPayment.fulfilled, (state, action) => {
        state.clientSecret = action.payload.client_secret;
        state.isLoadingPayment = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createPayment.pending, (state) => {
        state.isLoadingPayment = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.isLoadingPayment = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getProPrice.fulfilled, (state, action) => {
        state.proPrice = action.payload.unit_price;
        state.monthlyProPrice = action.payload.pro_individual_monthly_price;
        state.annualProPrice = action.payload.pro_individual_annual_price;
        state.monthlyProTeamsUnitPrice = action.payload.pro_teams_monthly_unit_price;
        state.annualProTeamsUnitPrice = action.payload.pro_teams_annual_unit_price;
        state.scribeProPriceId = action.payload.stripe_price_id;
        state.sessionUrl = action.payload.session_url;
        state.isLoadingPrice = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getProPrice.pending, (state) => {
        state.isLoadingPrice = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getProPrice.rejected, (state, action) => {
        state.isLoadingPrice = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getEssPrice.fulfilled, (state, action) => {
        state.entPriceItems = action.payload.price_items;
        state.sessionUrl = action.payload.session_url;
        state.isLoadingPrice = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getEssPrice.pending, (state) => {
        state.isLoadingPrice = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getEssPrice.rejected, (state, action) => {
        state.isLoadingPrice = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getSuperOrganizationInfo.fulfilled, (state, action) => {
        state.totalUsers = action.payload.total_users;
        state.totalDocs = action.payload.total_docs;
        state.organizations = action.payload.organizations;
        state.invoiceData = action.payload.invoice_data;
        state.promotion = action.payload.promotion;
        if (action.payload.plan_period) {
          state.currentPlan = action.payload.plan_period;
        }
        if (action.payload.plan_type) {
          state.currentPlanType = action.payload.plan_type;
        }
        state.promotionStart = action.payload.promotion_start;
        state.promotionDuration = action.payload.promotion_duration;
        state.promotionPercentOff = action.payload.promotion_percent_off;
        state.promotionTaxExempt = action.payload.promotion_tax_exempt;
        state.isCancelled = action.payload.is_cancelled;
        state.activePromotion = action.payload.active_promotion;
        state.activeTrial = action.payload.active_trial;
        state.activeTaxExemptTrial = action.payload.active_tax_exempt_trial;
        state.currentPeriodEnd = action.payload.current_period_end;
        state.nextInvoiceDate = action.payload.next_invoice_date;
        state.promotionActiveNextPeriod = action.payload.promotion_active_next_period;
        state.overrideSeatCount = action.payload.override_seat_count;
        state.purchasedSeats = action.payload.purchased_seats;
        state.hasBillingAddress = action.payload.has_billing_address;
        state.totalUsersWithoutScribeUsers = action.payload.total_users_without_scribe_users;
        state.canReactivate = action.payload.can_reactivate;
        state.scheduledToBeDowngraded = action.payload.scheduled_to_be_downgraded;
        state.paymentMethod = action.payload.payment_method;
        state.stripeCustomerId = action.payload.stripe_customer_id;
        state.isLoadingInfo = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getSuperOrganizationInfo.pending, (state) => {
        state.isLoadingInfo = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getSuperOrganizationInfo.rejected, (state, action) => {
        state.isLoadingInfo = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getCredits.fulfilled, (state, action) => {
        state.credits = action.payload.credits;
        state.isLoadingInfo = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getCredits.pending, (state) => {
        state.isLoadingInfo = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getCredits.rejected, (state, action) => {
        state.isLoadingInfo = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getNextBillingDate.fulfilled, (state, action) => {
        state.nextBillingDate = action.payload.billing_date;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getNextBillingDate.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getNextBillingDate.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(changePlan.fulfilled, (state, action) => {
        state.currentPlan = action.payload.plan_period;
        state.currentPlanType = action.payload.plan_type;
        if (action.payload.override_seat_count !== undefined) {
          state.overrideSeatCount = parseInt(action.payload.override_seat_count, 10);
        }
        state.isLoadingPlan = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(changePlan.pending, (state) => {
        state.isLoadingPlan = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(changePlan.rejected, (state, action) => {
        state.isLoadingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(changeSubscriptionPlan.fulfilled, (state, action) => {
        state.currentPlan = action.payload.plan_period;
        state.currentPlanType = action.payload.plan_type;
        state.isLoadingPlan = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(changeSubscriptionPlan.pending, (state) => {
        state.isLoadingPlan = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(changeSubscriptionPlan.rejected, (state, action) => {
        state.isLoadingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(downgrade.fulfilled, (state, action) => {
        state.isCancelled = action.payload.is_cancelled;
        state.isLoadingPlan = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(downgrade.pending, (state) => {
        state.isLoadingPlan = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(downgrade.rejected, (state, action) => {
        state.isLoadingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(reactivate.fulfilled, (state, action) => {
        state.isCancelled = false;
        state.isLoadingPlan = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(reactivate.pending, (state) => {
        state.isLoadingPlan = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(reactivate.rejected, (state, action) => {
        state.isLoadingPlan = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(applyPromotion.fulfilled, (state, action) => {
        state.promotion = action.payload.promotion;
        state.promotionDuration = action.payload.promotion_duration;
        state.promotionPercentOff = action.payload.percent_off;
        state.promotionTaxExempt = action.payload.tax_exempt;
        state.isLoadingPromo = false;
        state.promoCodeError = false;
        state.errorMessage = null;
      })
      .addCase(applyPromotion.pending, (state) => {
        state.isLoadingPromo = true;
        state.promoCodeError = false;
        state.errorMessage = null;
      })
      .addCase(applyPromotion.rejected, (state, action) => {
        state.isLoadingPromo = false;
        state.promoCodeError = true;
        state.errorMessage = action.payload;
      })
      .addCase(updateCustomerAddress.pending, (state) => {
        state.updateAddressPending = true;
        state.updateAddressError = false;
        state.updateAddressSuccess = false;
      })
      .addCase(updateCustomerAddress.fulfilled, (state) => {
        state.hasBillingAddress = true;
        state.updateAddressPending = false;
        state.updateAddressError = false;
        state.updateAddressSuccess = true;
      })
      .addCase(updateCustomerAddress.rejected, (state) => {
        state.updateAddressPending = false;
        state.updateAddressError = true;
        state.updateAddressSuccess = false;
      })
      .addCase(clearPromoCodeError, (state) => {
        state.promoCodeError = false;
      })
      .addCase(addPoNumber.pending, (state) => {
        state.addPoNumberPending = true;
        state.addPoNumberError = false;
        state.addPoNumberSuccess = false;
      })
      .addCase(addPoNumber.fulfilled, (state) => {
        state.addPoNumberPending = false;
        state.addPoNumberError = false;
        state.addPoNumberSuccess = true;
      })
      .addCase(addPoNumber.rejected, (state) => {
        state.addPoNumberPending = false;
        state.addPoNumberError = true;
        state.addPoNumberSuccess = false;
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

export const selectBilling = createSelector(
  [
    (state) => state.billing.credits,
    (state) => state.billing.clientSecret,
    (state) => state.billing.totalUsers,
    (state) => state.billing.totalDocs,
    (state) => state.billing.organizations,
    (state) => state.billing.invoiceData,
    (state) => state.billing.nextBillingDate,
    (state) => state.billing.currentPlan,
    (state) => state.billing.currentPlanType,
    (state) => state.billing.proPrice,
    (state) => state.billing.essPrice,
    (state) => state.billing.monthlyProPrice,
    (state) => state.billing.annualProPrice,
    (state) => state.billing.monthlyProTeamsUnitPrice,
    (state) => state.billing.annualProTeamsUnitPrice,
    (state) => state.billing.sessionUrl,
    (state) => state.billing.scribeProPriceId,
    (state) => state.billing.promotion,
    (state) => state.billing.promotionStart,
    (state) => state.billing.promotionDuration,
    (state) => state.billing.promotionPercentOff,
    (state) => state.billing.promotionTaxExempt,
    (state) => state.billing.isCancelled,
    (state) => state.billing.activePromotion,
    (state) => state.billing.activeTrial,
    (state) => state.billing.activeTaxExemptTrial,
    (state) => state.billing.currentPeriodEnd,
    (state) => state.billing.nextInvoiceDate,
    (state) => state.billing.promotionActiveNextPeriod,
    (state) => state.billing.promoCodeError,
    (state) => state.billing.overrideSeatCount,
    (state) => state.billing.purchasedSeats,
    (state) => state.billing.isError,
    (state) => state.billing.isLoading,
    (state) => state.billing.isLoadingPromo,
    (state) => state.billing.isLoadingPlan,
    (state) => state.billing.isLoadingPrice,
    (state) => state.billing.isLoadingInfo,
    (state) => state.billing.errorMessage,
    (state) => state.billing.updateAddressError,
    (state) => state.billing.updateAddressPending,
    (state) => state.billing.updateAddressSuccess,
    (state) => state.billing.hasBillingAddress,
    (state) => state.billing.entPriceItems,
    (state) => state.billing.totalUsersWithoutScribeUsers,
    (state) => state.billing.addPoNumberError,
    (state) => state.billing.addPoNumberPending,
    (state) => state.billing.addPoNumberSuccess,
    (state) => state.billing.canReactivate,
    (state) => state.billing.paymentMethod,
    (state) => state.billing.stripeCustomerId,
    (state) => state.billing.scheduledToBeDowngraded
  ],
  /**
   * @returns {BillingStateType}
   */
  (
    credits,
    clientSecret,
    totalUsers,
    totalDocs,
    organizations,
    invoiceData,
    nextBillingDate,
    currentPlan,
    currentPlanType,
    proPrice,
    essPrice,
    monthlyProPrice,
    annualProPrice,
    monthlyProTeamsUnitPrice,
    annualProTeamsUnitPrice,
    sessionUrl,
    scribeProPriceId,
    promotion,
    promotionStart,
    promotionDuration,
    promotionPercentOff,
    promotionTaxExempt,
    isCancelled,
    activePromotion,
    activeTrial,
    activeTaxExemptTrial,
    currentPeriodEnd,
    nextInvoiceDate,
    promotionActiveNextPeriod,
    promoCodeError,
    overrideSeatCount,
    purchasedSeats,
    isError,
    isLoading,
    isLoadingPromo,
    isLoadingPlan,
    isLoadingPrice,
    isLoadingInfo,
    errorMessage,
    updateAddressError,
    updateAddressPending,
    updateAddressSuccess,
    hasBillingAddress,
    entPriceItems,
    totalUsersWithoutScribeUsers,
    addPoNumberError,
    addPoNumberPending,
    addPoNumberSuccess,
    canReactivate,
    paymentMethod,
    stripeCustomerId,
    scheduledToBeDowngraded
  ) => ({
    credits,
    clientSecret,
    totalUsers,
    totalDocs,
    organizations,
    invoiceData,
    nextBillingDate,
    currentPlan,
    currentPlanType,
    proPrice,
    essPrice,
    monthlyProPrice,
    annualProPrice,
    monthlyProTeamsUnitPrice,
    annualProTeamsUnitPrice,
    sessionUrl,
    scribeProPriceId,
    promotion,
    promotionStart,
    promotionDuration,
    promotionPercentOff,
    promotionTaxExempt,
    isCancelled,
    activePromotion,
    activeTrial,
    activeTaxExemptTrial,
    currentPeriodEnd,
    nextInvoiceDate,
    promotionActiveNextPeriod,
    promoCodeError,
    overrideSeatCount,
    purchasedSeats,
    isError,
    isLoading,
    isLoadingPromo,
    isLoadingPlan,
    isLoadingPrice,
    isLoadingInfo,
    errorMessage,
    updateAddressError,
    updateAddressPending,
    updateAddressSuccess,
    hasBillingAddress,
    entPriceItems,
    totalUsersWithoutScribeUsers,
    addPoNumberError,
    addPoNumberPending,
    addPoNumberSuccess,
    canReactivate,
    paymentMethod,
    stripeCustomerId,
    scheduledToBeDowngraded
  })
);

export const selectCheckout = createSelector(
  [
    (state) => state.billing.credits,
    (state) => state.billing.clientSecret,
    (state) => state.billing.totalUsers,
    (state) => state.billing.entPriceItems,
    (state) => state.billing.currentPlanType,
    (state) => state.billing.promotion,
    (state) => state.billing.promotionStart,
    (state) => state.billing.promotionDuration,
    (state) => state.billing.promotionPercentOff,
    (state) => state.billing.promotionTaxExempt,
    (state) => state.billing.promoCodeError,
    (state) => state.billing.overrideSeatCount,
    (state) => state.billing.isLoading,
    (state) => state.billing.isLoadingPromo,
    (state) => state.billing.isLoadingPlan,
    (state) => state.billing.isLoadingPrice,
    (state) => state.billing.isLoadingInfo,
    (state) => state.billing.errorMessage
  ],
  (
    credits,
    clientSecret,
    totalUsers,
    entPriceItems,
    currentPlanType,
    promotion,
    promotionStart,
    promotionDuration,
    promotionPercentOff,
    promotionTaxExempt,
    promoCodeError,
    overrideSeatCount,
    isLoading,
    isLoadingPromo,
    isLoadingPlan,
    isLoadingPrice,
    isLoadingInfo,
    errorMessage
  ) => ({
    credits,
    clientSecret,
    totalUsers,
    entPriceItems,
    currentPlanType,
    promotion,
    promotionStart,
    promotionDuration,
    promotionPercentOff,
    promotionTaxExempt,
    promoCodeError,
    overrideSeatCount,
    isLoading,
    isLoadingPromo,
    isLoadingPlan,
    isLoadingPrice,
    isLoadingInfo,
    errorMessage
  })
);

export default billingSlice.reducer;
